import { fetchAdminUser } from '@/api/admin';
import RoleBadge from '@/components/admin/role-badge';
import EntityHeroCard from '@/components/navigation/entity-hero-card';
import { Spinner } from '@/components/ui/spinner';
import type { AdminUserTab, IAdminUserDetail } from '@/entities/admin-user.entity';
import { PageScroll } from '@/layouts/page-layout';
import { BanIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import BuildsTab from './builds-tab';
import ClientsTab from './clients-tab';
import LandingsTab from './landings-tab';
import ManageTab from './manage-tab';
import PartnerWorkersSection from './partner-workers-section';
import WorkerPartnersSection from './worker-partners-section';
import ProfitsTab from './profits-tab';
import SenderTab from './sender-tab';
import UserStats from './user-stats';
import UserTabs from './user-tabs';

export default function AdminUserDetailPage() {
	const params = useParams();
	const [searchParams] = useSearchParams();
	const profileId = Number(params.profileId);
	const [user, setUser] = useState<IAdminUserDetail | null>(null);
	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState<AdminUserTab>('clients');

	useEffect(() => {
		const tab = searchParams.get('tab');
		if (
			tab === 'clients' ||
			tab === 'builds' ||
			tab === 'profits' ||
			tab === 'landings' ||
			tab === 'sender' ||
			tab === 'manage'
		) {
			setActiveTab(tab);
		}
	}, [searchParams]);

	const loadUser = useCallback(async () => {
		if (!Number.isFinite(profileId)) return;
		setLoading(true);
		try {
			const data = await fetchAdminUser(profileId);
			setUser(data);
		} finally {
			setLoading(false);
		}
	}, [profileId]);

	useEffect(() => {
		loadUser();
	}, [loadUser]);

	if (!Number.isFinite(profileId)) {
		return <div className='p-4 text-muted-foreground text-sm'>Некорректный ID пользователя</div>;
	}

	if (loading || !user) {
		return (
			<div className='h-full flex items-center justify-center'>
				<Spinner />
			</div>
		);
	}

	const isBanned = user.isBanned || user.userIsBanned;

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<EntityHeroCard
				title={`#${user.nickname}`}
				trailing={
					<div className='flex items-center gap-1.5 shrink-0'>
						{isBanned && (
							<span className='inline-flex items-center gap-1 text-xs text-destructive font-medium'>
								<BanIcon size={12} />
								Забанен
							</span>
						)}
						<RoleBadge role={user.role} />
					</div>
				}
			/>
			<UserStats user={user} />
			{user.role === 'partner' && <PartnerWorkersSection user={user} />}
			{user.role === 'worker' && <WorkerPartnersSection user={user} />}
			<UserTabs activeTab={activeTab} onTabChange={setActiveTab} userRole={user.role} />

			{activeTab === 'clients' && <ClientsTab profileId={user.id} />}
			{activeTab === 'builds' && <BuildsTab key={user.id} profileId={user.id} />}
			{activeTab === 'profits' && (
				<ProfitsTab key={user.id} profileId={user.id} nickname={user.nickname} onProfitCreated={loadUser} />
			)}
			{activeTab === 'landings' && <LandingsTab key={user.id} profileId={user.id} />}
			{activeTab === 'sender' && <SenderTab key={user.id} profileId={user.id} />}
			{activeTab === 'manage' && <ManageTab user={user} onUpdated={setUser} />}
		</PageScroll>
	);
}
